import React, { useState } from 'react';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { Mail, BookOpen, PenTool, MessageCircle } from 'lucide-react';
import { submitToNewsletter } from '../lib/newsletter';
import { navigate } from '../lib/navigation';
export function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    const success = await submitToNewsletter(email);
    if (success) {
      setStatus('success');
      setEmail('');
    } else {
      setStatus('error');
    }
  };
  const benefits = [{
    icon: <PenTool className="w-5 h-5" data-id="element-148" />,
    title: 'Practical Tools',
    desc: 'Managing anxiety and people-pleasing patterns'
  }, {
    icon: <MessageCircle className="w-5 h-5" data-id="element-149" />,
    title: 'Real Reflections',
    desc: 'Acceptance, change, and showing up as yourself'
  }, {
    icon: <Mail className="w-5 h-5" data-id="element-150" />,
    title: 'Behind the Scenes',
    desc: 'Thoughts on therapy and mental health'
  }, {
    icon: <BookOpen className="w-5 h-5" data-id="element-151" />,
    title: 'Curated Resources',
    desc: 'Books, articles, and tools worth your time'
  }];
  return <section id="newsletter" className="py-20 md:py-32 bg-gradient-to-br from-teal to-sage text-white" data-id="element-152">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-153">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-154">
          {/* Content Side */}
          <div className="space-y-8" data-id="element-155">
            <div data-id="element-156">
              <h2 className="text-3xl md:text-4xl font-medium mb-4" data-id="element-157">
                Thoughts on arriving at yourself
              </h2>
              <p className="text-lg text-white/90 leading-relaxed max-w-xl" data-id="element-158">
                Weekly insights on anxiety, self-acceptance, and building a life
                that feels like yours. No fluff, no toxic positivity, just real
                talk.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-159">
              {benefits.map((benefit, i) => <div key={i} className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:bg-white/20 transition-colors" data-id="element-160">
                  <div className="mb-3 text-terracotta" data-id="element-161">{benefit.icon}</div>
                  <h3 className="font-semibold mb-1" data-id="element-162">{benefit.title}</h3>
                  <p className="text-sm text-white/80" data-id="element-163">{benefit.desc}</p>
                </div>)}
            </div>
          </div>

          {/* Form Side */}
          <div className="bg-white/10 backdrop-blur-md p-8 md:p-10 rounded-2xl border border-white/20 shadow-xl max-w-md mx-auto w-full" data-id="element-164">
            <div className="text-center mb-8" data-id="element-165">
              <p className="text-sm font-medium uppercase tracking-wider text-terracotta mb-2" data-id="element-166">
                Weekly (ish) · Free
              </p>
              <h3 className="text-2xl font-medium" data-id="element-167">Join the conversation</h3>
              <p className="text-white/70 text-sm mt-2" data-id="element-168">
                Like a conversation with a therapist friend
              </p>
            </div>

            {status === 'success' ? <div className="text-center py-8 animate-fade-in-up" data-id="element-169">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-170">
                  <Mail className="w-8 h-8 text-white" data-id="element-171" />
                </div>
                <h4 className="text-xl font-medium mb-2" data-id="element-172">
                  You're on the list!
                </h4>
                <p className="text-white/80" data-id="element-173">
                  Thanks for joining. Keep an eye on your inbox.
                </p>
                <Button variant="ghost" className="mt-6 text-white hover:bg-white/10" onClick={() => setStatus('idle')} data-id="element-174">
                  Send to another email
                </Button>
              </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-175">
                <Input type="email" placeholder="name@example.com" label="Email address" required value={email} onChange={e => setEmail(e.target.value)} data-id="element-176" />
                <Button type="submit" className="w-full bg-terracotta hover:bg-terracotta-hover text-white border-none" isLoading={status === 'loading'} data-id="element-177">
                  Yes, send me the good stuff
                </Button>
                {status === 'error' && <p className="text-xs text-red-200 text-center" data-id="element-178">
                    Something went wrong. Please try again.
                  </p>}
                <p className="text-xs text-white/60 text-center mt-4" data-id="element-179">
                  I'll never spam you or share your email. Unsubscribe anytime.
                  <br data-id="element-180" />
                  <a href="#/privacy-policy" onClick={e => {
                e.preventDefault();
                navigate('#/privacy-policy');
              }} className="underline hover:text-white transition-colors mt-1 inline-block" data-id="element-181">
                    Privacy Policy
                  </a>
                </p>
              </form>}
          </div>
        </div>
      </div>
    </section>;
}